import { IconButton, Stack, Typography } from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import { ArrowBack, Clear } from '@mui/icons-material';
import { useDisclosure } from '@shared/hooks';
import { HelpCenterPaper } from './HelpCenterPaper';
import { ButtonBreadcrumbItem, ButtonBreadcrumbs } from '../Breadcrumbs/ButtonBreadcrumbs';
import { useSharedTFunction } from '@shared/i18n';
import { SearchButton } from './SearchButton';
import { ArticleMinimal, CategoryState } from '@shared/types';

interface Props {
  onClose: () => void;
  titleClassName?: string;

  CategoryArticlesComponent: React.ComponentType<{
    category: CategoryState;
    onArticleClick: (article: ArticleMinimal) => void;
    onCategoryClick: (category: CategoryState) => void;
  }>;

  TopLevelCategoriesComponent: React.ComponentType<{
    onCategoryClick: (category: CategoryState) => void;
    onClose: () => void;
  }>;

  HelpArticleComponent: React.ComponentType<{
    slug: string;
  }>;

  SearchContainerComponent: React.ComponentType<{
    onArticleClick: (article: ArticleMinimal) => void;
  }>;
}

export const HelpCenter: React.FC<Props> = ({
  onClose,
  titleClassName,
  CategoryArticlesComponent,
  TopLevelCategoriesComponent,
  HelpArticleComponent,
  SearchContainerComponent,
}) => {
  const t = useSharedTFunction();
  const [selectedCategories, setSelectedCategories] = React.useState<CategoryState[]>([]);
  const [selectedArticle, setSelectedArticle] = React.useState<ArticleMinimal>();
  const { isOpen: isSearchOpen, onOpen: onOpenSearch, onClose: onCloseSearch } = useDisclosure();

  const goBack = useCallback(() => {
    if (selectedArticle) setSelectedArticle(undefined);
    else if (isSearchOpen) onCloseSearch();
    else setSelectedCategories(selectedCategories.slice(0, -1));
  }, [isSearchOpen, onCloseSearch, selectedArticle, selectedCategories]);

  const selectCategory = useCallback((category: CategoryState) => {
    setSelectedCategories((prev) => [...prev, category]);
  }, []);

  const onArticleClickFromSearch = useCallback(
    (article: ArticleMinimal) => {
      setSelectedArticle(article);
      onCloseSearch();
    },
    [onCloseSearch]
  );

  const breadcrumbs: ButtonBreadcrumbItem[] = useMemo(() => {
    if (!selectedCategories.length) return [];

    const lastItem: ButtonBreadcrumbItem = selectedArticle
      ? {
          title: selectedArticle.title,
          id: selectedArticle.slug,
        }
      : selectedCategories[selectedCategories.length - 1];

    const firstCategories = selectedArticle ? selectedCategories : selectedCategories.slice(0, -1);

    return [
      {
        title: t('shared.helpCenter.home'),
        id: 'home',
        onClick: () => {
          setSelectedCategories([]);
          setSelectedArticle(undefined);
        },
      },
      ...firstCategories.map((category) => ({
        title: category.title,
        id: category.id,
        onClick: () => {
          const thisIndex = selectedCategories.indexOf(category);
          setSelectedCategories(selectedCategories.slice(0, thisIndex + 1));
          setSelectedArticle(undefined);
        },
      })),
      { title: lastItem.title, id: lastItem.id },
    ];
  }, [selectedArticle, selectedCategories, t]);

  return (
    <HelpCenterPaper>
      <Stack
        direction="row"
        alignItems="center"
        position="sticky"
        top="0"
        pb={1}
        sx={{ zIndex: 1, bgcolor: 'inherit' }}
      >
        <IconButton
          onClick={goBack}
          sx={{
            visibility:
              selectedCategories.length || selectedArticle || isSearchOpen ? undefined : 'hidden',
            flexGrow: 0,
          }}
          aria-label={t('shared.helpCenter.goBack')}
        >
          <ArrowBack />
        </IconButton>

        <Typography
          variant="h3"
          component="h1"
          sx={{ flexGrow: 1, textAlign: 'center', cursor: 'move', paddingTop: 2 }}
          className={titleClassName}
        >
          {t('shared.helpCenter.help')}
        </Typography>

        <IconButton
          onClick={onClose}
          sx={{ flexGrow: 0 }}
          aria-label={t('shared.helpCenter.close')}
        >
          <Clear />
        </IconButton>
      </Stack>
      <Stack flexGrow={1}>
        {isSearchOpen ? (
          <SearchContainerComponent onArticleClick={onArticleClickFromSearch} />
        ) : (
          <>
            {breadcrumbs.length > 0 && <ButtonBreadcrumbs items={breadcrumbs} maxItems={3} />}
            {selectedArticle ? (
              <HelpArticleComponent slug={selectedArticle.slug} />
            ) : selectedCategories.length ? (
              <CategoryArticlesComponent
                category={selectedCategories[selectedCategories.length - 1]}
                onArticleClick={setSelectedArticle}
                onCategoryClick={selectCategory}
              />
            ) : (
              <TopLevelCategoriesComponent onCategoryClick={selectCategory} onClose={onClose} />
            )}
          </>
        )}
      </Stack>

      {!selectedArticle && selectedCategories.length === 0 && !isSearchOpen && (
        <Stack position="sticky" bottom={0} m={2} sx={{ zIndex: 1, bgcolor: 'inherit' }}>
          <SearchButton onClick={onOpenSearch} />
        </Stack>
      )}
    </HelpCenterPaper>
  );
};
